import type { FreelancerSearchFilters } from "@/domains/company/active-search/types";
import {
  parseSubcategoryCsv,
  toSubcategoryCsv,
} from "@/domains/company/active-search/utils/subcategory-filter";
import { boundSemanticSearch, boundSkillsCsv } from "@/libs/skill-input";
import type { MatchPoolQuery } from "./types";

// Default page size for the match endpoint (contract: default 20, max 100).
export const DEFAULT_MATCH_PER_PAGE = 20;

const clampPositiveInteger = (
  value: number | undefined,
  fallback: number,
  max?: number,
): number | undefined => {
  if (value === undefined) return undefined;
  if (!Number.isFinite(value)) return fallback;
  const positive = Math.max(1, Math.floor(value ?? fallback));
  return max === undefined ? positive : Math.min(max, positive);
};

// Serialized query params handed to axios (which URL-encodes them). Mirrors the
// list endpoint's `SerializedListParams`.
type SerializedMatchParams = Record<string, string | number>;

// Pure mapper: URL-derived search filters + the store's authoritative offer id + a
// page number → the twin `MatchPoolQuery`. Shared fields pass through BY NAME (the
// match endpoint is the request twin of POST /freelancers/search); empties are
// dropped so the query key stays minimal and stable. `job_offer_id` is ALWAYS the
// passed-in store value, never `filters.job_offer_id`. `experience_level` is kept
// (D13, twin-parity) even though the BE currently no-ops it. `skills` is already a
// CSV string on `FreelancerSearchFilters`, so it passes through unchanged.
export const matchQueryFromFilters = (
  filters: FreelancerSearchFilters,
  jobOfferId: string,
  page: number,
): MatchPoolQuery => {
  const query: MatchPoolQuery = {
    job_offer_id: jobOfferId,
    page,
    per_page: DEFAULT_MATCH_PER_PAGE,
  };

  // The string-valued optional filter keys (excludes job_offer_id/page/per_page).
  type StringFilterKey =
    | "search"
    | "job_category"
    | "job_subcategory"
    | "skills"
    | "work_preference"
    | "price_per_day_min"
    | "price_per_day_max"
    | "weekly_hours"
    | "available_from"
    | "experience_level";

  const add = (key: StringFilterKey, value: string | undefined | null) => {
    if (value === undefined || value === null || value === "") return;
    query[key] = value;
  };

  add("search", boundSemanticSearch(filters.search));
  add("job_category", filters.job_category);
  if (filters.job_category) {
    add(
      "job_subcategory",
      toSubcategoryCsv(parseSubcategoryCsv(filters.job_subcategory)),
    );
  }
  add("skills", boundSkillsCsv(filters.skills));
  add("work_preference", filters.work_preference);
  add("price_per_day_min", filters.price_per_day_min);
  add("price_per_day_max", filters.price_per_day_max);
  add("weekly_hours", filters.weekly_hours);
  add("available_from", filters.available_from);
  add("experience_level", filters.experience_level);

  return query;
};

// Pure param builder — drops every undefined / null / empty-string param (ATS
// "defaults omitted" idiom, same as `buildListPoolParams`). Idempotent with
// `matchQueryFromFilters`; axios-free so it stays unit-testable in the node env.
export const buildMatchPoolParams = (
  query: MatchPoolQuery,
): SerializedMatchParams => {
  const params: SerializedMatchParams = {};

  const add = (key: string, value: string | number | null | undefined) => {
    if (value === undefined || value === null || value === "") return;
    params[key] = value;
  };

  add("job_offer_id", query.job_offer_id);
  add("page", clampPositiveInteger(query.page, 1));
  add(
    "per_page",
    clampPositiveInteger(query.per_page, DEFAULT_MATCH_PER_PAGE, 100),
  );
  add("search", boundSemanticSearch(query.search));
  add("job_category", query.job_category);
  if (query.job_category) {
    add(
      "job_subcategory",
      toSubcategoryCsv(parseSubcategoryCsv(query.job_subcategory)),
    );
  }
  add("skills", boundSkillsCsv(query.skills));
  add("work_preference", query.work_preference);
  add("price_per_day_min", query.price_per_day_min);
  add("price_per_day_max", query.price_per_day_max);
  add("weekly_hours", query.weekly_hours);
  add("available_from", query.available_from);
  add("experience_level", query.experience_level);

  return params;
};
